import React from 'react';
import { Loader2, X } from 'lucide-react';
import { Avatar, Badge } from '../ui';
import { RFQCollaborator } from '../../types';

/**
 * One person on an RFQ's access list. The owner and a collaborator are the
 * same row with a different right-hand side — one badge that cannot be
 * removed, one role plus a remove control — so the two never drift apart
 * visually.
 */
export interface AccessRowProps {
  person: RFQCollaborator;
  /** Renders the remove control. Omitted for the owner and for non-owners. */
  onRemove?: (userId: string) => void;
  busy?: boolean;
}

/** The address is worth showing only when it is not already the name. */
const secondaryLine = (person: RFQCollaborator): string | null =>
  person.email && person.email !== person.name ? person.email : null;

export const AccessRow: React.FC<AccessRowProps> = ({ person, onRemove, busy = false }) => {
  const name = person.name ?? 'Someone on your team';
  const secondary = secondaryLine(person);

  return (
    <div className="flex items-center gap-s4 rounded-card border border-line-divider px-s4 py-s3">
      <Avatar name={name} size="md" />
      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-row-title text-ink">{name}</p>
        {secondary && <p className="m-0 truncate text-meta text-ink-muted">{secondary}</p>}
      </div>
      {person.role === 'OWNER' ? (
        <Badge tone="action">Owner</Badge>
      ) : (
        <span className="text-body-sm text-ink-secondary">Editor</span>
      )}
      {onRemove && person.role !== 'OWNER' && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onRemove(person.userId)}
          aria-label={`Remove ${name}'s access`}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-control border border-transparent text-ink-muted transition-colors hover:border-line hover:bg-neutral-bg hover:text-danger-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-action-border disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-[13px] w-[13px] animate-spin" /> : <X className="h-[14px] w-[14px]" />}
        </button>
      )}
    </div>
  );
};

export default AccessRow;
